// babel polyfill has been superseded by including core-js and regenerator-runtime directly
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import 'media-base-ui/styles/common/app.scss';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import authStateChecker from '@nhl/auth-state-checker';
import { setAuthToken as setContentModuleAuthToken } from '@nhl/nhl-react-ui/lib/ContentModule';
import { setAuthToken as setMenuAuthToken } from '@nhl/nhl-react-ui/lib/Menu';
import fetchHelpers from '@nhl/fetch-helpers';

import { sessionOperations } from 'media-base-ui/redux/ducks/session'
import { withComponentSeasons, withAuthentication } from 'media-base-ui/views/enhancers';
import { initializeLibrary } from 'media-base-ui/utils/fontAwesomeInitializer';

import configureStore from './redux/store';
import * as layouts from './views/layouts';

// Get nonce from the root javascript bundle that is generated when we build our client code
const cspNonce = document.querySelector('#root-bundle').nonce;

// NB: Do not alter this line! I know your IDE is complaining. This will be used by styled-components to set a nonce
// attribute on injected CSS. It has to be set 'globally' but doing window.__webpack_nonce__ doesn't work for some reason.
__webpack_nonce__ = cspNonce;

// Set the nonce for emotion based style injection (namely coming from material-ui, and react-select).
const emotionCache = createCache({
    key: 'media-account-ui',
    nonce: cspNonce,
});
window.emotionCache = emotionCache;

// Make sure this is before any other `fontawesome` API calls
config.autoAddCss = false;

const initializeApp = async () => {
    initializeLibrary();

    const store = configureStore({});

    const EnhancedApp = withAuthentication(withComponentSeasons(layouts.App), false);

    // accountCookieData is set in SitePage.moustache
    authStateChecker.init(accountCookieData.ssoCookieName, accountCookieData.ssoCookieDomain);

    const isLoggedIn = await authStateChecker.checkAuthState();
    const authToken = authStateChecker.getAuthToken();

    store.dispatch(sessionOperations.userLoginStatusResponse(isLoggedIn, null));

    if (isLoggedIn) {
        setContentModuleAuthToken(`Bearer ${authToken}`);
        setMenuAuthToken(`Bearer ${authToken}`);
        fetchHelpers.setAuthToken(authToken);
    }

    const container = document.getElementById('root');
    const root = createRoot(container);

    root.render(
        <CacheProvider value={emotionCache}>
            <Provider store={store}>
                <HelmetProvider context={{}}>
                    <BrowserRouter basename='/' future={{}}>
                        <Routes>
                            <Route path="/public/*" element={<layouts.PublicApp />} />
                            <Route path="*" element={<EnhancedApp />} />
                        </Routes>
                    </BrowserRouter>
                </HelmetProvider>
            </Provider>
        </CacheProvider>
    );
};

// Initializing rollbar
if (process.env.NODE_ENV === 'production') {
    import('rollbar').then(({ default: Rollbar }) => {
        window.Rollbar = new Rollbar(window.rollbarConfig);
        initializeApp();
    });
} else {
    initializeApp();
}
